import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useMCPTasks } from '../hooks/useMCPTasks';
import { useMCPStocks } from '../hooks/useMCPStocks';
import { useMCPBooks } from '../hooks/useMCPBooks';
import Footer from './Footer';

const MorePage = () => {
  const navigate = useNavigate();
  const { tasks = [], loading: tasksLoading } = useMCPTasks();
  const { stocks = [], loading: stocksLoading } = useMCPStocks();
  const { books = [], loading: booksLoading } = useMCPBooks();

  // Debug logging
  console.log('MorePage - Tasks:', tasks);
  console.log('MorePage - Books:', books);
  console.log('MorePage - Stocks:', stocks);

  // Calculate real metrics with better error handling
  const completedTasks = Array.isArray(tasks) ? tasks.filter(task => task && task.completed === true).length : 0;
  const completedBooks = Array.isArray(books) ? books.filter(book => book && book.status === 'completed').length : 0;
  const totalStocks = Array.isArray(stocks) ? stocks.length : 0;
  
  // Calculate portfolio value - handle different data structures and missing data
  const portfolioValue = Array.isArray(stocks) ? stocks.reduce((total, stock) => {
    if (!stock || typeof stock !== 'object') return total;
    
    // Handle different possible property names for price and shares
    const currentPrice = parseFloat(stock.currentPrice || stock.price || stock.purchasePrice || 0);
    const shares = parseFloat(stock.shares || stock.quantity || 0);
    const value = currentPrice * shares;
    
    console.log(`Stock ${stock.symbol || 'Unknown'}: price=${currentPrice}, shares=${shares}, value=${value}`);
    return total + (isNaN(value) ? 0 : value);
  }, 0) : 0;

  // Debug the calculated values
  console.log('MorePage - Calculated values:', {
    completedTasks,
    completedBooks,
    totalStocks,
    portfolioValue
  });

  return (
    <div className="font-sans text-slate-800 antialiased" style={{ backgroundColor: '#f8fafc', color: '#0f172a' }}>
      <style>{`
        :root { 
          --brand-900: #0f172a; 
          --muted: #64748b; 
        }
        body { 
          background-color: #f8fafc; 
          color: var(--brand-900); 
        }
        h1,h2,h3,h4 { 
          color: var(--brand-900); 
        }
        .rounded-image { 
          border-radius: 12px; 
        }
        .site-container { 
          max-width: 1100px; 
          margin-left: auto; 
          margin-right: auto; 
        }
      `}</style>

      <main>
        {/* Hero */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img src="image2_1.jpg" alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-slate-900/70"></div>
          </div>
          <div className="site-container px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative z-10">
              <h1 className="text-4xl md:text-5xl font-extrabold leading-tight mb-4 text-white">More Trackers</h1>
              <p className="text-lg text-slate-200 mb-6">Additional tracking tools to help you monitor every aspect of your life. Boost productivity, manage finances, and track your learning journey.</p>
              <div className="flex gap-4 flex-wrap">
                <button
                  onClick={() => navigate('/tasks')}
                  className="inline-flex items-center rounded-md bg-slate-900 px-5 py-3 text-white shadow"
                >
                  Tasks
                </button>
                <button
                  onClick={() => navigate('/stocks')}
                  className="inline-flex items-center rounded-md border border-white px-5 py-3 text-white hover:bg-white/10 transition-colors"
                >
                  Stocks
                </button>
                <button
                  onClick={() => navigate('/books')}
                  className="inline-flex items-center rounded-md border border-white px-5 py-3 text-white hover:bg-white/10 transition-colors"
                >
                  Books
                </button>
                <button
                  onClick={() => navigate('/notes')}
                  className="inline-flex items-center rounded-md border border-white px-5 py-3 text-white hover:bg-white/10 transition-colors"
                >
                  Notes
                </button>
                <button
                  onClick={() => navigate('/news')}
                  className="inline-flex items-center rounded-md border border-white px-5 py-3 text-white hover:bg-white/10 transition-colors"
                >
                  News
                </button>
              </div>
              <div className="mt-6">
                <button
                  onClick={() => navigate('/track')}
                  className="inline-flex items-center rounded-md border border-white px-6 py-2 text-white hover:bg-white/10 transition-colors text-sm"
                >
                  ← Back to Track
                </button>
              </div>
              <ul className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm text-slate-200">
                <li className="text-center"><strong className="text-white text-xl">4</strong><div>More Trackers</div></li>
                <li className="text-center"><strong className="text-white text-xl">Productivity</strong><div>Focus</div></li>
                <li className="text-center"><strong className="text-white text-xl">Learning</strong><div>Growth</div></li>
              </ul>
            </div>
            <div className="relative z-10 flex justify-center lg:justify-end">
              <div className="w-full max-w-md bg-white/10 backdrop-blur-sm rounded-image p-6 shadow-2xl">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-white font-semibold">Productivity Hub</h3>
                    <p className="text-slate-200 text-sm">Manage your work and learning</p>
                  </div>
                  <div className="flex gap-2">
                    <button className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"></path>
                      </svg>
                    </button>
                    <button className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="rounded-lg bg-white/20 p-4">
                    <p className="text-slate-200 text-sm mb-1">Tasks Done</p>
                    <div className="text-2xl font-bold text-white">
                      {tasksLoading ? '...' : (completedTasks || 0)}
                    </div>
                    <p className="text-emerald-300 text-sm mt-1">This week</p>
                  </div>
                  <div className="rounded-lg bg-white/20 p-4">
                    <p className="text-slate-200 text-sm mb-1">Books Read</p>
                    <div className="text-2xl font-bold text-white">
                      {booksLoading ? '...' : (completedBooks || 0)}
                    </div>
                    <p className="text-emerald-300 text-sm mt-1">This month</p>
                  </div>
                </div>
                <div className="rounded-lg bg-white/20 p-4 mb-6">
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="text-white font-medium">Portfolio Value</h4>
                    <button className="text-sm text-slate-200">View All</button>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-200 text-sm">Total Value</span>
                      <span className="text-white font-semibold">
                        {stocksLoading ? '...' : `$${(portfolioValue || 0).toLocaleString()}`}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-200 text-sm">Today's Change</span>
                      <span className="text-emerald-300 font-semibold">
                        {stocksLoading ? '...' : '+0.0%'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-200 text-sm">Active Stocks</span>
                      <span className="text-white font-semibold">
                        {stocksLoading ? '...' : (totalStocks || 0)}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <button className="rounded-lg bg-white/20 p-3 text-center">
                    <span className="text-sm text-slate-200">Tasks</span>
                  </button>
                  <button className="rounded-lg bg-white/20 p-3 text-center">
                    <span className="text-sm text-slate-200">Stocks</span>
                  </button>
                  <button className="rounded-lg bg-white/20 p-3 text-center">
                    <span className="text-sm text-slate-200">Books</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features / Trackers */}
        <section id="features" className="py-16">
          <div className="site-container px-6">
            <h2 className="text-2xl font-bold mb-6">Additional Trackers</h2>
            <p className="text-slate-600 mb-8">Productivity and learning tools to help you stay organized and grow.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
              <div className="p-6 bg-white rounded-lg shadow-sm">
                <h3 className="font-semibold mb-2">Task Management</h3>
                <p className="text-sm text-slate-600">Organize your daily tasks and boost productivity with smart task management.</p>
              </div>
              <div className="p-6 bg-white rounded-lg shadow-sm">
                <h3 className="font-semibold mb-2">Stock Monitoring</h3>
                <p className="text-sm text-slate-600">Track your investments and monitor market performance in real-time.</p>
              </div>
              <div className="p-6 bg-white rounded-lg shadow-sm">
                <h3 className="font-semibold mb-2">Reading Progress</h3>
                <p className="text-sm text-slate-600">Manage your reading list and track your learning journey.</p>
              </div>
              <div className="p-6 bg-white rounded-lg shadow-sm">
                <h3 className="font-semibold mb-2">Notes & Ideas</h3>
                <p className="text-sm text-slate-600">Capture your thoughts, ideas, and important information in one place.</p>
              </div>
            </div>

            <h3 className="text-xl font-bold mb-4">Quick Access</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="p-4 bg-white rounded-lg shadow-sm">
                <h4 className="font-semibold">Task Manager</h4>
                <p className="text-sm text-slate-600">AI-powered productivity</p>
              </div>
              <div className="p-4 bg-white rounded-lg shadow-sm">
                <h4 className="font-semibold">Stocks Watcher</h4>
                <p className="text-sm text-slate-600">Real-time market monitoring</p>
              </div>
              <div className="p-4 bg-white rounded-lg shadow-sm">
                <h4 className="font-semibold">Books Tracker</h4>
                <p className="text-sm text-slate-600">Manage your reading list</p>
              </div>
              <div className="p-4 bg-white rounded-lg shadow-sm">
                <h4 className="font-semibold">Notes App</h4>
                <p className="text-sm text-slate-600">Capture your thoughts</p>
              </div>
            </div>
          </div>
        </section>

        {/* Visual section */}
        <section className="py-16 bg-slate-50">
          <div className="site-container px-6 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-xl font-bold mb-4">Boost your productivity</h3>
              <p className="text-slate-600 mb-6">See trends, insights, and the next actions that matter. Built for people who want meaningful progress in their work and learning.</p>
            </div>
            <div>
              <img src="image2.jpg" srcSet="image2.jpg 800w, image2.jpg 1200w" alt="App screenshot" className="w-full rounded-image shadow-md object-cover" loading="lazy" decoding="async" width="800" height="500" />
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-white">
          <div className="site-container px-6">
            <div className="text-center">
              <h3 className="text-xl font-bold mb-2">Ready to Get Organized?</h3>
              <p className="text-slate-600 mb-6">Start managing your tasks, investments, and learning with our comprehensive tracking platform.</p>
              <button
                onClick={() => navigate('/track')}
                className="inline-flex items-center rounded-md bg-slate-900 px-6 py-3 text-white"
              >
                Back to Core Trackers
              </button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default MorePage;
